<template>
    <div class="fieldset-cloneable">
        <div class="fieldset-cloneable-add" v-if="showAddButton && 'top' === addButtonPosition">
            <slot name="addButton" :add="add">
                <div class="button" v-interact="add">Weiteres Feld hinzufügen</div>
            </slot>
        </div>
        <vue-nestable v-if="nestable" :value="model" v-bind="nestableOptions" ref="nestable" @input="setModel" @change="$emit('change', $event)">
            <template #default="{item, index}">
                <div v-if="!customDragSelector" class="flex">
                    <vue-nestable-handle :item="item" class="flex flex-vertical-middle">
                        <i class="visible-md-upward material-icons drag-handle" aria-hidden="true" role="img">open_with</i>
                    </vue-nestable-handle>
                    <div :id="getUid(item)" class="full-width fieldset-slot-container">
                        <slot name="render" :values="item" :$index="index" :is-first="!index" :is-last="isLastNestedItem(item, index)" :manage="manageMethods"/>
                    </div>
                </div>
                <slot v-else name="render" :id="getUid(item)" :values="item" :$index="index" :is-first="!index" :is-last="isLastNestedItem(item, index)" :manage="manageMethods"/>
            </template>
            <template #placeholder>&nbsp;</template>
        </vue-nestable>
        <app-draggable v-else-if="sortable" v-model="model" v-bind="dragOptions" @start="$emit('start', $event)" @change="$emit('change', $event)" item-key="news-link.id">
            <template #item="{element, index}">
                <div :class="classes" :id="element.__fieldset_uid" v-show="!element.__fieldset_hidden">
                    <div class="flex">
                        <div v-if="sortable && !customDragSelector" class="flex flex-vertical-middle">
                            <i class="visible-md-upward material-icons drag-handle" aria-hidden="true" role="img">open_with</i>
                        </div>
                        <div class="full-width fieldset-slot-container">
                            <slot name="render" :values="element" :$index="index" :is-first="!index" :is-last="index === modelValue.length - 1" :manage="manageMethods"/>
                        </div>
                    </div>
                </div>
            </template>
        </app-draggable>
        <div v-else-if="renderReversed" @start="$emit('start', $event)" @change="$emit('change', $event)">
            <div v-for="(values, $index) in modelValue.toReversed()" :key="getUid(values)" :class="classes" :id="values.__fieldset_uid" v-show="!values.__fieldset_hidden">
                <div class="flex">
                    <div class="full-width fieldset-slot-container">
                        <slot name="render" :values="values" :$index="getReversedIndex($index)" :is-first="!getReversedIndex($index)" :is-last="getReversedIndex($index) === modelValue.length - 1" :manage="manageMethods"/>
                    </div>
                </div>
            </div>
        </div>
        <div v-else @start="$emit('start', $event)" @change="$emit('change', $event)">
            <div v-for="(values, $index) in modelValue" :key="getUid(values)" :class="classes" :id="values.__fieldset_uid" v-show="!values.__fieldset_hidden">
                <div class="flex">
                    <div class="full-width fieldset-slot-container">
                        <slot name="render" :values="values" :$index="$index" :is-first="!$index" :is-last="$index === modelValue.length - 1" :manage="manageMethods"/>
                    </div>
                </div>
            </div>
        </div>

        <slot name="subContent"/>

        <div class="fieldset-cloneable-add" v-if="showAddButton && 'bottom' === addButtonPosition">
            <slot name="addButton" :add="add">
                <div class="button" v-interact="add">Weiteres Feld hinzufügen</div>
            </slot>
        </div>
    </div>
</template>

<script lang="ts">
    import {queryFocusableElements} from '@/library/focus';
    import {type ComponentPublicInstance} from 'vue';
    import {Component, Prop, Vue, Watch} from 'vue-facing-decorator';
    import {VueNestable, VueNestableHandle} from 'vue3-nestable';
    import {uniqueId} from '../library/string';
    import {swap} from '../library/utils';
    import {deepCopyObject} from '../utils/portuna';
    import AppDraggable from './AppDraggable.vue';
    import FormRow from './form/FormRow.vue';

    type ValueType = {
        id?: number;
        delete?: 0 | 1;
        __fieldset_uid?: string;
    };

    type ValueTypeNested = ValueType & {
        children?: ValueTypeNested[];
    };

    type NestedFieldsetResult = { parent: ValueTypeNested[]; index: number; path: number[] };

    @Component({
        name: 'FieldsetCloneable',
        components: {
            AppDraggable,
            VueNestable,
            VueNestableHandle,
        },
        emits: ['change', 'start', 'update:modelValue'],
    })
    export default class FieldsetCloneable extends Vue
    {
        readonly $refs: {
            nestable: ComponentPublicInstance,
        };

        @Prop({
            type: Array,
            default()
            {
                return [];
            },
            required: true,
        }) modelValue: ValueType[];

        @Prop({
            type: [Object, Function],
            default()
            {
                return {};
            },
        }) defaultValues: {} | (() => {});

        @Prop({
            type: Boolean,
            default: false,
            required: false,
        }) sortable: boolean;

        @Prop({
            type: Array,
            default: [],
            required: false,
        }) customClasses: string[];

        // this option does currently not provide nested-indexes such as "fieldset[0][1][5][element_name]" @see path in findNestedFieldset
        @Prop({
            type: Boolean,
            default: false,
            required: false,
        }) nestable: boolean;

        // see https://github.com/rhwilr/vue-nestable
        @Prop({
            type: Object,
            default: () => ({}),
            required: false,
        }) nestableOptions: Record<string, any>;

        @Prop({
            type: String,
            default: '',
            required: false,
        }) group: string;

        @Prop({
            type: Boolean,
            default: false,
            required: false,
        }) noDefaultInputField: boolean;

        @Prop({
            type: [Boolean, String],
            default: false,
            required: false,
        }) customDragSelector: boolean | string;

        @Prop({
            type: String,
            default: 'bottom',
            required: false,
        }) addButtonPosition: 'top' | 'bottom';

        @Prop({
            type: Boolean,
            default: false,
            required: false,
        }) focusFirstInput: boolean;

        // this option outputs the entries in reverse order while preserving the original index
        @Prop({
            type: Boolean,
            default: false,
            required: false,
        }) renderReversed: boolean;

        model: ValueType[] = [];

        get manageMethods()
        {
            let methods: IFieldsetCloneableManageMethods;

            if (this.nestable)
            {
                methods = {
                    delete: this.deleteFieldsetNested,
                    markDelete: this.markDelete,
                    clear: this.clearFieldsetNested,
                    moveUp: this.moveUpNested,
                    moveDown: this.moveDownNested,
                };
            }
            else
            {
                methods = {
                    add: this.add,
                    delete: this.deleteFieldset,
                    markDelete: this.markDelete,
                    clear: this.clearFieldset,
                    moveUp: this.moveUp,
                    moveDown: this.moveDown,
                };
            }

            return methods;
        }

        get dragOptions(): {}
        {
            if (this.sortable)
            {
                return {
                    group: this.group || this._uid,
                    'force-fallback': true,
                    handle: this.customDragSelector || '.drag-handle',
                };
            }
            else
            {
                return {};
            }
        }

        get classes()
        {
            let classes = ['fieldset-cloneable-group'];

            if (this.sortable)
            {
                classes.push('sortable');
            }

            if (Array.isArray(this.customClasses) && this.customClasses.length > 0)
            {
                classes.push(...this.customClasses);
            }

            return classes;
        }

        // consider using reactive method @see: PortunaBox.showAdditionally
        get showAddButton()
        {
            return !!(this.$slots.addButton);
        }

        @Watch('modelValue', {deep: true})
        watchValue(): void
        {
            this.model = this.modelValue;

            this.observeEmptySet();
        }

        @Watch('model', {deep: true})
        watchModel(): void
        {
            this.$emit('update:modelValue', this.model);
        }

        created(): void
        {
            this.model = this.modelValue;
            this.observeEmptySet();
        }

        observeEmptySet()
        {
            if (!this.noDefaultInputField && this.modelValue.length === 0)
            {
                this.add();
            }
        }

        deleteFieldset($index: number)
        {
            if (this.modelValue.length === 1 && !this.noDefaultInputField)
            {
                this.clearFieldset(0);
            }
            else
            {
                this.removeFieldsetErrors($index);
                this.$nextTick(() => this.modelValue.splice($index, 1));
            }
        }

        removeFieldsetErrors(index: number)
        {
            const fieldset = document.getElementById(this.modelValue[index].__fieldset_uid!);

            this.removeErrorsWithinElement(fieldset);
        }

        removeFieldsetNestedErrors(result: NestedFieldsetResult)
        {
            let fieldset: Nullable<Element>;

            // follow nested path
            if (this.customDragSelector)
            {
                let node: Nullable<Element> = this.$refs.nestable.$el;

                for (let path of result.path)
                {
                    node = node?.querySelector(':scope > ol.nestable-list')?.children.item(path) || null;
                }

                fieldset = node?.querySelector(':scope > .nestable-item-content') || null;
            }
            // get by fieldset-uid
            else
            {
                fieldset = document.getElementById(result.parent[result.index].__fieldset_uid!);
            }

            this.removeErrorsWithinElement(fieldset);
        }

        removeErrorsWithinElement(fieldset: Nullable<Element>)
        {
            if (!fieldset)
            {
                return;
            }

            for (const formRowElement of fieldset.querySelectorAll('.form-row'))
            {
                const formRow = formRowElement.__vue__ as FormRow;

                if (formRow)
                {
                    this.$store.dispatch('form/resetError', {errorName: formRow._errorName});
                }
            }
        }

        markDelete($index: number)
        {
            if (!this.modelValue[$index].id)
            {
                this.deleteFieldset($index);
                return;
            }

            this.modelValue[$index] = Object.assign(this.modelValue[$index], {delete: this.modelValue[$index].delete ? 0 : 1});
        }

        moveUp($index: number)
        {
            if (!this.sortable || this.modelValue.length <= 1 || !$index)
            {
                return;
            }

            swap(this.modelValue, $index, $index - 1);
        }

        moveDown($index: number)
        {
            if (!this.sortable || this.modelValue.length <= 1 || $index === this.modelValue.length - 1)
            {
                return;
            }

            swap(this.modelValue, $index, $index + 1);
        }

        moveUpNested(item: object)
        {
            const result = this.findNestedFieldset(this.model, item);

            if (!result || result.parent.length <= 1 || !result.index)
            {
                return;
            }

            swap(result.parent, result.index, result.index - 1);
        }

        moveDownNested(item: object)
        {
            const result = this.findNestedFieldset(this.model, item);

            if (!result || result.parent.length <= 1 || result.index === result.parent.length - 1)
            {
                return;
            }

            swap(result.parent, result.index, result.index + 1);
        }

        getDefaultValues()
        {
            let defaultValues = 'function' === typeof this.defaultValues ? this.defaultValues() : this.defaultValues;

            return deepCopyObject(defaultValues);
        }

        clearFieldset($index: number)
        {
            this.removeFieldsetErrors($index);
            this.modelValue[$index] = this.getDefaultValues();
        }

        findNestedFieldset(values: ValueTypeNested[], search: ValueTypeNested, path: number[] = []): NestedFieldsetResult | false
        {
            let result: NestedFieldsetResult | false = false;

            for (let key in values)
            {
                if (!values.hasOwnProperty(key))
                {
                    continue;
                }

                if (values[key] === search)
                {
                    result = {
                        parent: values,
                        index: parseInt(key),
                        path: [...path, parseInt(key)],
                    };

                    break;
                }

                if (Array.isArray(values[key].children))
                {
                    result = this.findNestedFieldset(values[key].children!, search, [...path, parseInt(key)]);

                    if (result)
                    {
                        break;
                    }
                }
            }

            return result;
        }

        deleteFieldsetNested(item: object)
        {
            const result = this.findNestedFieldset(this.model, item);

            if (result)
            {
                if (result.parent === this.model && this.model.length === 1)
                {
                    this.clearFieldset(0);
                }
                else
                {
                    this.removeFieldsetNestedErrors(result);
                    this.$nextTick(() => result.parent.splice(result.index, 1));
                }
            }
        }

        clearFieldsetNested(item: ValueTypeNested)
        {
            const result = this.findNestedFieldset(this.model, item);

            if (result)
            {
                this.removeFieldsetNestedErrors(result);

                result.parent[result.index] = Object.assign(
                    this.getDefaultValues(),
                    item.children ? {children: item.children} : {}
                );
            }
        }

        getUid(entry: any)
        {
            if (!entry.__fieldset_uid)
            {
                entry.__fieldset_uid = uniqueId('field-');
            }

            return entry.__fieldset_uid;
        }

        add()
        {
            const values = this.getDefaultValues();
            this.modelValue.push(values);

            if (this.focusFirstInput)
            {
                this.$nextTick(() =>
                {
                    const cloneableGroup = this.$el.querySelectorAll('.fieldset-cloneable-group');

                    if (!cloneableGroup || !cloneableGroup.length)
                    {
                        return;
                    }

                    const focusableElement = queryFocusableElements(cloneableGroup[cloneableGroup.length - 1]).item(0);

                    if (typeof focusableElement.__vue__ !== 'undefined' && typeof focusableElement.__vue__.triggerFocus === 'function')
                    {
                        focusableElement.__vue__.triggerFocus();
                    }
                    else
                    {
                        focusableElement.focus();
                    }
                });
            }

            return values;
        }

        isLastNestedItem(item: object, index: number)
        {
            const result = this.findNestedFieldset(this.model, item);

            return result && index === result.parent.length - 1;
        }

        setModel(value: ValueType[])
        {
            this.model = value;
        }

        getReversedIndex(index: number): number
        {
            return this.modelValue.length - 1 - index;
        }
    };
</script>